/**
 * Help Center Article Page
 * Displays individual help articles with markdown rendering
 */

import type { LoaderFunctionArgs } from 'react-router';
import { useLoaderData, useSearchParams, useNavigation } from 'react-router';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  Text,
  InlineStack,
  Badge,
  SkeletonPage,
  SkeletonBodyText,
  SkeletonDisplayText,
} from '@shopify/polaris';
import { ClockIcon } from '@shopify/polaris-icons';
import type { Env } from '../../src/types';
import { getArticleBySlug, categoryLabels, helpArticles } from '../utils/help-center';
import { getArticleContent } from '../content/help-articles';
import { HelpNavigation } from '../components/help/HelpNavigation';
import { MarkdownWithVisuals } from '../components/help/MarkdownWithVisuals';
import { getEnv } from '~/utils/cloudflare-context';

export async function loader({ params, context, request }: LoaderFunctionArgs) {
  const { slug } = params;
  const env = getEnv(context);
  const url = new URL(request.url);
  const shop = url.searchParams.get('shop');

  if (!slug) {
    throw new Response('Not Found', { status: 404 });
  }

  const article = getArticleBySlug(slug);

  if (!article) {
    throw new Response('Article Not Found', { status: 404 });
  }

  // Load article content from bundled markdown
  let content = getArticleContent(slug) || '# Content Loading Error\n\nUnable to load article content. Please try again later.';

  // Handle if content still has export statement (Vite import issue)
  if (content.startsWith('export default')) {
    // Extract the string content from the export statement
    const match = content.match(/export default ["'](.+)["'];?$/s);
    if (match) {
      content = match[1]
        .replace(/\\n/g, '\n')  // Convert \n to actual newlines
        .replace(/\\"/g, '"')   // Convert \" to "
        .replace(/\\'/g, "'");  // Convert \' to '
    }
  }

  // Track article view
  if (shop) {
    try {
      const shopResult = await env.DB.prepare('SELECT id FROM shops WHERE shop_domain = ?').bind(shop).first();

      if (shopResult) {
        await env.DB.prepare(
          `INSERT INTO help_center_views (shop_id, article_slug, article_title, session_id)
           VALUES (?, ?, ?, ?)`
        )
          .bind(shopResult.id, slug, article.title, 'session-' + Date.now())
          .run();
      }
    } catch (error) {
      // View tracking failed - non-critical analytics, silent fail
    }
  }

  return {
    article: {
      ...article,
      content,
    },
  };
}

// MarkdownRenderer replaced by MarkdownWithVisuals component

export default function HelpArticle() {
  const { article } = useLoaderData<typeof loader>();
  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  const queryString = searchParams.toString();

  // Page loading state
  const isPageLoading = navigation.state === 'loading';

  // Show skeleton page while loading
  if (isPageLoading) {
    return (
      <SkeletonPage
        title="Loading article..."
        backAction
        fullWidth
      >
        <Layout>
          <Layout.Section variant="oneThird">
            <Card>
              <BlockStack gap="300">
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText lines={8} />
              </BlockStack>
            </Card>
          </Layout.Section>
          <Layout.Section>
            <BlockStack gap="400">
              <Card>
                <SkeletonBodyText lines={2} />
              </Card>
              <Card>
                <BlockStack gap="400">
                  <SkeletonDisplayText size="large" />
                  <SkeletonBodyText lines={6} />
                  <SkeletonBodyText lines={4} />
                  <SkeletonBodyText lines={5} />
                </BlockStack>
              </Card>
            </BlockStack>
          </Layout.Section>
        </Layout>
      </SkeletonPage>
    );
  }

  return (
    <Page
      fullWidth
      title={article.title}
      subtitle={article.description}
      backAction={{
        content: 'Help Center',
        url: `/app/help${queryString ? `?${queryString}` : ''}`,
      }}
    >
      <Layout>
        {/* Navigation Sidebar */}
        <Layout.Section variant="oneThird">
          <Card>
            <HelpNavigation articles={helpArticles} queryString={queryString} />
          </Card>
        </Layout.Section>

        {/* Article Content */}
        <Layout.Section>
          <BlockStack gap="400">
            {/* Article Metadata */}
            <Card>
              <InlineStack gap="400">
                <Badge tone="info">{categoryLabels[article.category]}</Badge>
                <InlineStack gap="100" blockAlign="center">
                  <ClockIcon />
                  <Text as="span" variant="bodySm" tone="subdued">
                    {article.readTime} min read
                  </Text>
                </InlineStack>
              </InlineStack>
            </Card>

            {/* Article Content */}
            <Card>
              <MarkdownWithVisuals content={article.content} />
            </Card>

            {/* Was This Helpful? */}
            <Card>
              <BlockStack gap="300">
                <Text as="h3" variant="headingMd" fontWeight="semibold">
                  Was this article helpful?
                </Text>
                <InlineStack gap="200">
                  <ui-button variant="secondary">Yes, this helped</ui-button>
                  <ui-button variant="secondary">No, still need help</ui-button>
                </InlineStack>
              </BlockStack>
            </Card>

            {/* CTA Card for Cloudflare Infrastructure */}
            {article.slug === 'cloudflare-infrastructure' && (
              <Card>
                <div
                  style={{
                    position: 'relative',
                    minHeight: '200px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  {/* Background Image */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: 'url(/images/tech-support.png)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      opacity: 0.15,
                      zIndex: 0,
                    }}
                  />

                  {/* Content */}
                  <div style={{ position: 'relative', zIndex: 1, padding: '24px' }}>
                    <BlockStack gap="400">
                      <Text as="h3" variant="headingLg" fontWeight="bold">
                        Need Technical Support?
                      </Text>
                      <Text as="p" variant="bodyLg">
                        Have questions about Cloudflare infrastructure, performance optimization, or deployment? Our technical team is here to help.
                      </Text>
                      <InlineStack gap="300">
                        <ui-button variant="primary">Contact Support</ui-button>
                        <ui-button variant="secondary">View Documentation</ui-button>
                      </InlineStack>
                    </BlockStack>
                  </div>
                </div>
              </Card>
            )}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
